import React, { useCallback, useEffect, useState } from "react";
import Icon from "@imminently/decisively-ui/Icon";
import { getSessionHistory, type SessionChangeTimelineEntry } from "@/app/providers/services";
import Dialog from "@imminently/decisively-ui/Dialog";
import { DialogHeader } from "@/ui/dialogs/DialogHeader";
import LoadingOverlay from "@imminently/decisively-ui/LoadingOverlay";
import { JsonEditor } from "@/common";
import TemporalValue from "@imminently/decisively-ui/TemporalValue";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  Avatar,
  AvatarGroup,
  Tooltip as MuiTooltip,
} from "@mui/material";
import Button from "@imminently/decisively-ui/Button";
import moment from "moment";

function isTemporal(value: unknown): boolean {
  return value != null && typeof value === "object" && "temporal" in value;
}

function formatValue(value: unknown): string {
  if (value === undefined || value === null) return "—";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  try {
    const s = JSON.stringify(value);
    return s.length > 120 ? `${s.slice(0, 117)}...` : s;
  } catch {
    return String(value);
  }
}

function formatValueFull(value: unknown): string {
  if (value === undefined || value === null) return "—";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

/** Who object shape from history API */
interface WhoObject {
  full_name?: string;
  on_behalf_of?: string;
  roles?: string[];
  clientIP?: string;
  clientIp?: string;
  identity_id?: string;
  name?: string;
}

function getWhoDisplay(who: unknown): string {
  if (who == null) return "—";
  if (typeof who === "string") return who;
  if (typeof who === "object" && who !== null) {
    const w = who as WhoObject;
    const fullName = w.full_name ?? w.name ?? w.identity_id ?? "";
    if (typeof w.on_behalf_of === "string" && w.on_behalf_of) {
      return fullName ? `${w.on_behalf_of} (via ${fullName})` : w.on_behalf_of;
    }
    return fullName || "—";
  }
  return String(who);
}

function getWhoRoles(who: unknown): string[] {
  if (who == null || typeof who !== "object") return [];
  const w = who as WhoObject;
  const r = w.roles;
  return Array.isArray(r) ? r.filter((x): x is string => typeof x === "string") : [];
}

function getWhoClientIP(who: unknown): string {
  if (who == null || typeof who !== "object") return "";
  const w = who as WhoObject;
  const ip = w.clientIP ?? w.clientIp;
  return typeof ip === "string" ? ip : "";
}

export interface AttributeHistoryModalProps {
  open: boolean;
  onClose: () => void;
  sessionId: string;
  attributePath: string;
  attributeLabel?: string;
  interactions?: Array<{ id: string; mode?: string; comment?: string; data?: Record<string, unknown> }>;
  /** When set, closing the modal and navigating to Input tab with this interaction selected will be triggered on comment link click */
  onGoToInteraction?: (interactionId: string) => void;
}

export const AttributeHistoryModal = ({
  open,
  onClose,
  sessionId,
  attributePath,
  attributeLabel,
  interactions = [],
  onGoToInteraction,
}: AttributeHistoryModalProps) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeline, setTimeline] = useState<SessionChangeTimelineEntry[]>([]);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const loadHistory = useCallback(async () => {
    if (!open || !sessionId || !attributePath) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getSessionHistory(sessionId, attributePath);
      setTimeline(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(e?.message ?? "Failed to load history");
      setTimeline([]);
    } finally {
      setLoading(false);
    }
  }, [open, sessionId, attributePath]);

  useEffect(() => {
    if (open) loadHistory();
  }, [open, loadHistory]);

  const getInteractionInfo = (interactionId: string) => {
    const index = interactions.findIndex((i) => i.id === interactionId);
    const interaction = index >= 0 ? interactions[index] : null;
    const indexNum = index >= 0 ? index + 1 : null;
    const mode = interaction?.mode ?? "—";
    const comment = interaction?.comment ?? "—";
    const interactionData = interaction?.data ?? {};
    return { mode, comment, indexNum, interaction, interactionData };
  };

  const handleCommentClick = (interactionId: string) => {
    onClose();
    onGoToInteraction?.(interactionId);
  };

  const toggleExpanded = (key: string) => {
    setExpandedKey((prev) => (prev === key ? null : key));
  };

  return (
    <Dialog.Content className="max-w-[90vw] w-[90vw] max-h-[85vh] flex flex-col !p-0">
      <DialogHeader title={`Change history: ${attributeLabel || attributePath}`} controls={{ allowResize: true }} />
      <Dialog.Body>
        <div className="flex-1 overflow-hidden flex flex-col relative min-h-[200px]">
        {loading && <LoadingOverlay message="Loading history…" />}
        {error && !loading && (
          <div className="p-4 text-amber-600">
            <Typography variant="body2">{error}</Typography>
          </div>
        )}
        {!loading && !error && timeline.length === 0 && (
          <div className="p-4 text-gray-500">
            <Typography variant="body2">No changes recorded for this attribute.</Typography>
          </div>
        )}
        {!loading && !error && timeline.length > 0 && (
          <TableContainer component={Paper} className="flex-1 overflow-auto m-4 mr-8">
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ width: 40 }} />
                  <TableCell sx={{ width: 56 }}>#</TableCell>
                  <TableCell>When</TableCell>
                  <TableCell>Comment</TableCell>
                  <TableCell>Old value</TableCell>
                  <TableCell>New value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {timeline.map((entry) => {
                  const key = `${entry.when}-${entry.interactionId}`;
                  const isExpanded = expandedKey === key;
                  const { mode, comment, indexNum, interactionData } = getInteractionInfo(entry.interactionId);
                  const oldTemporal = isTemporal(entry.oldValue);
                  const newTemporal = isTemporal(entry.newValue);

                  return (
                    <React.Fragment key={key}>
                      <TableRow
                        hover
                        sx={{ cursor: "pointer", "&:hover": { backgroundColor: "action.hover" } }}
                        onClick={() => toggleExpanded(key)}
                      >
                        <TableCell padding="checkbox" sx={{ width: 40 }} onClick={(e) => e.stopPropagation()}>
                          <Button icon
                            size="sm"
                            label={isExpanded ? "Collapse" : "Expand"}
                            onClick={() => toggleExpanded(key)}
                          >
                            {isExpanded ? <Icon name="expand" /> : <Icon name="go-forward" />}
                          </Button>
                        </TableCell>
                        <TableCell sx={{ width: 56 }}>{indexNum ?? "—"}</TableCell>
                        <TableCell>{moment(entry.when).format("YYYY-MM-DD HH:mm:ss")}</TableCell>
                        <TableCell>
                          <span className="max-w-[300px] truncate block" title={comment}>
                            {comment}
                          </span>
                        </TableCell>
                        <TableCell>
                          {oldTemporal ? (
                            <Chip size="small" label="Temporal" />
                          ) : (
                            <span className="max-w-[140px] truncate block" title={formatValue(entry.oldValue)}>
                              {formatValue(entry.oldValue)}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          {newTemporal ? (
                            <Chip size="small" label="Temporal" />
                          ) : (
                            <span className="max-w-[140px] truncate block" title={formatValue(entry.newValue)}>
                              {formatValue(entry.newValue)}
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                      {isExpanded && (() => {
                        const roles = getWhoRoles(entry.who);
                        const clientIP = getWhoClientIP(entry.who);
                        return (
                          <TableRow>
                            <TableCell colSpan={6} sx={{ padding: 2, backgroundColor: "grey.50", verticalAlign: "top" }}>
                              <div className="flex flex-col gap-4">
                                <div className="grid grid-cols-[auto_1fr_auto_1fr] gap-y-1 gap-x-4 items-baseline self-start">
                                  {/* Row 1: Who | value | Mode | value */}
                                  <Typography variant="caption" color="textSecondary">
                                    Who
                                  </Typography>
                                  <Typography variant="body2">{getWhoDisplay(entry.who)}</Typography>
                                  <Typography variant="caption" color="textSecondary">
                                    Mode
                                  </Typography>
                                  <Typography variant="body2">{mode}</Typography>
                                  {/* Row 2: Roles | value | Interaction | value */}
                                  <Typography variant="caption" color="textSecondary">
                                    Roles
                                  </Typography>
                                  <div>
                                    {roles.length > 0 ? (
                                      <MuiTooltip title={roles.join(", ")} placement="top">
                                        <AvatarGroup max={8} sx={{ justifyContent: "flex-start" }}>
                                          {roles.map((role, i) => (
                                            <Avatar
                                              key={`${role}-${i}`}
                                              sx={{ width: 24, height: 24, fontSize: "0.75rem", bgcolor: "primary.main" }}
                                            >
                                              {role.slice(0, 1).toUpperCase()}
                                            </Avatar>
                                          ))}
                                        </AvatarGroup>
                                      </MuiTooltip>
                                    ) : (
                                      <Typography variant="body2">—</Typography>
                                    )}
                                  </div>
                                  <Typography variant="caption" color="textSecondary">
                                    Interaction
                                  </Typography>
                                  <div>
                                    {onGoToInteraction ? (
                                      <Typography
                                        component="button"
                                        variant="body2"
                                        type="button"
                                        onClick={() => handleCommentClick(entry.interactionId)}
                                        sx={{
                                          color: "text.primary",
                                          textDecoration: "underline",
                                          cursor: "pointer",
                                          background: "none",
                                          border: "none",
                                          padding: 0,
                                          textAlign: "left",
                                          "&:hover": { textDecoration: "underline" },
                                        }}
                                      >
                                        Go to interaction #{indexNum ?? entry.interactionId}
                                      </Typography>
                                    ) : (
                                      <Typography variant="body2">#{indexNum ?? entry.interactionId}</Typography>
                                    )}
                                  </div>
                                  {/* Row 3: Client IP | value | | */}
                                  <Typography variant="caption" color="textSecondary">
                                    Client IP
                                  </Typography>
                                  <Typography variant="body2">{clientIP || "—"}</Typography>
                                  <div />
                                  <div />
                                </div>
                                <div>
                                  <Typography variant="caption" color="textSecondary" className="block mb-1">
                                    Interaction data
                                  </Typography>
                                  <div className="border border-border rounded overflow-hidden" style={{ height: 240 }}>
                                    <JsonEditor
                                      value={JSON.stringify(interactionData, null, 2)}
                                      disabled
                                      withCopy
                                    />
                                  </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <Typography variant="caption" color="textSecondary" className="block mb-1">
                                      Old value
                                    </Typography>
                                    <div className="border border-border rounded p-2 min-h-[60px] overflow-auto bg-background">
                                      {oldTemporal ? (
                                        <TemporalValue value={entry.oldValue as any} />
                                      ) : (
                                        <pre className="text-sm whitespace-pre-wrap break-words m-0">
                                          {formatValueFull(entry.oldValue)}
                                        </pre>
                                      )}
                                    </div>
                                  </div>
                                  <div>
                                    <Typography variant="caption" color="textSecondary" className="block mb-1">
                                      New value
                                    </Typography>
                                    <div className="border border-border rounded p-2 min-h-[60px] overflow-auto bg-background">
                                      {newTemporal ? (
                                        <TemporalValue value={entry.newValue as any} />
                                      ) : (
                                        <pre className="text-sm whitespace-pre-wrap break-words m-0">
                                          {formatValueFull(entry.newValue)}
                                        </pre>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })()}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        </div>
      </Dialog.Body>
    </Dialog.Content>
  );
};
